import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { Dropdown } from "../../components/ui/dropdown";
import { PasswordField } from "./password-field";
import { formatRoleName } from "./team-page";
import type { CrmMember } from "./types";

const fieldCls =
  "w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm text-ink outline-none focus:border-brand";
const labelCls = "mb-1.5 block text-xs font-semibold text-ink-2";

export function EditUserModal({ user, roles, onClose, onSave }: {
  user: CrmMember;
  /** Active, assignable roles — the dropdown falls back to the builtin two
   * if this is empty (e.g. still loading). */
  roles: { id: string; name: string }[];
  onClose: () => void;
  onSave: (data: { name: string; email: string; phone: string; role: string; password?: string }) => Promise<void>;
}) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone);
  const [role, setRole] = useState(user.role);
  // Passwords are stored as a one-way hash — there's no real value to show
  // here, and pre-filling a placeholder like "demo1234" would silently
  // reset it to that if Save is clicked without noticing. Stays blank
  // unless the admin actually wants to set a new one.
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (saving) return;
    setSaving(true); setError("");
    try {
      await onSave({ name: name.trim(), email: email.trim(), phone: phone.trim(), role, password: password.trim() || undefined });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit user"
      subtitle="Change their details or role."
      onClose={onClose}
      width="max-w-lg"
      footer={
        <>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">
            Cancel
          </button>
          <button disabled={saving} onClick={submit} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Full name *</label>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Phone *</label>
            <input minLength={8} value={phone} onChange={e => setPhone(e.target.value)} className={fieldCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={fieldCls} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Role</label>
            <Dropdown
              size="sm"
              ariaLabel="User role"
              value={role}
              options={[
                ...(roles.some(r => r.id === role) ? [] : [{ value: role, label: formatRoleName(role) }]),
                ...roles.map(r => ({ value: r.id, label: r.name })),
              ]}
              onChange={setRole}
            />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <PasswordField minLength={8} placeholder="Leave blank to keep current" value={password} onChange={setPassword} className={fieldCls} />
          </div>
        </div>

        {error && <p role="alert" className="text-xs font-semibold text-red-500">{error}</p>}
      </div>
    </Modal>
  );
}
